/** Shared enums and config — manifest Section 18 */

export const TILE_SIZE = 64;
export const MAP_GRID_APPROX_WIDTH = 76;
export const MAP_GRID_APPROX_HEIGHT = 42;

/** Layer A placement grid — must match client world tile counts (see `client/src/game/map-world-dims.ts`). */
export const LAYER_A_GRID_W = MAP_GRID_APPROX_WIDTH;
export const LAYER_A_GRID_H = MAP_GRID_APPROX_HEIGHT;
/** Inner margin (tiles) reserved from each edge for Layer A placement and walkable border. */
export const LAYER_A_MARGIN_TILES = 4;
/** Bump when layout placement rules change; stale Mongo `layerA` without this value is regenerated. */
export const LAYER_A_LAYOUT_REVISION = 13;

/** Target downtown façades along the road (story + decor fillers). */
export const LAYER_A_DOWNTOWN_BUILDING_TARGET = 6;
/** Target Maple Heights façades along the road (story + decor fillers). */
export const LAYER_A_SUBURB_BUILDING_TARGET = 4;
export const BUILDING_SLOTS_PER_MAP = 15;
export const NPC_SLOTS_PER_MAP = 25;

/** Min Chebyshev tile distance between NPCs in the same filler congregation group. */
export const NPC_CONGREGATION_MIN_TILE_DIST = 2;

export const DISCOVERY_RADIUS_TILES = 3;
export const NPC_INTERACT_RADIUS_TILES = 2;
export const BUILDING_INTERACT_RADIUS_TILES = 2.4;
export const SOLO_ONLY_EXCLUSION_RADIUS_TILES = 5;
export const MAX_WAYPOINTS_PER_PLAYER = 5;
export const WAYPOINT_LABEL_MAX_CHARS = 20;
export const MAX_PINNED_CLUES = 3;

export const MIN_EVIDENCE_FOR_ACCUSATION = 3;
export const TRIO_ACCUSATION_MAJORITY = 2;

/** Duo/trio: vote to enter accusation Case File. */
export const ACCUSATION_VOTE_SECONDS = 30;
/** Duo/trio: arrest / warrant phase after vote passes. */
export const ACCUSATION_ARREST_SECONDS = 180;

/** Delay before peers see a disconnected detective leave (brief mobile drops). */
export const PLAYER_DISCONNECT_GRACE_MS = 30_000;
/** Erase game session after no sockets in the game room for this long. */
export const EMPTY_GAME_SESSION_MS = 5 * 60_000;
/** How often the server checks for vacant sessions to delete. */
export const SESSION_CLEANUP_INTERVAL_MS = 60_000;
/** Delete briefing sessions that never reached `active` and have no game sockets. */
export const BRIEFING_ABANDON_MS = 30 * 60_000;

/** Delete idle open lobbies (never started). */
export const LOBBY_OPEN_STALE_MS = 60 * 60_000;
/** Delete closed lobbies with no linked session (abandoned start / leave). */
export const LOBBY_CLOSED_NO_SESSION_STALE_MS = 60 * 60_000;
/** Delete closed lobbies that still reference a finished or torn-down session. */
export const LOBBY_CLOSED_WITH_SESSION_STALE_MS = 24 * 60 * 60_000;
/** How often lobby janitor runs. */
export const LOBBY_CLEANUP_INTERVAL_MS = 15 * 60_000;
/** When pruning duplicate closed rows per city code + host, keep the newest this long. */
export const LOBBY_CLOSED_DUPLICATE_KEEP_MS = 5 * 60_000;

/** Socket.IO engine heartbeat — client pingTimeout must exceed server pingInterval. */
export const SOCKET_PING_INTERVAL_MS = 25_000;
export const SOCKET_PING_TIMEOUT_MS = 60_000;

/** Multiplayer idle pose relay so mobile/proxies do not drop the socket. */
export const PLAYER_IDLE_KEEPALIVE_MS = 20_000;

export const SEED_TARGET_PER_PLAYER_COUNT = 10;
/** Per lobby member who finished this seed at this player count (multiplayer weighting). */
export const USER_PLAYED_SEED_WEIGHT_FACTOR = 0.1;
export const SEED_GEN_CONCURRENCY = 3;
export const DEEPSEEK_MAX_RETRIES = 2;

export const PLAYER_COUNTS = [1, 2, 3] as const;

export const RADIO_QUICK_PHRASES_STATIC = [
  "Come here",
  "I found something",
  "This witness is suspicious",
  "This building is locked",
  "I need you in the other zone",
  "I think I know who did it",
  "Let's make our accusation",
] as const;

export const NPC_DIRECTIONS = [
  "east",
  "south-east",
  "south",
  "south-west",
  "west",
  "north-west",
  "north",
  "north-east",
] as const;

export const NPC_SPRITE_SIZE = 64;
export const NPC_DIALOG_DISPLAY_SIZE = 128;
/** On-screen size for detectives and outdoor NPCs (~1.25 tiles). */
export const WORLD_CHARACTER_DISPLAY_PX = Math.round(TILE_SIZE * 1.25);
export const BUILDING_IMAGE_SIZE = 400;
export const BUILDING_FOOTPRINT_TILES = 6;

/** Building interior sub-map (tile grid) — compact rooms vs outdoor zone scale. */
export const INTERIOR_GRID_W = 16;
export const INTERIOR_GRID_H = 12;
/** E-key / tap radius at interior exit chevron (tiles). */
export const INTERIOR_EXIT_INTERACT_TILES = 2.6;
/** On-screen building scale — keep in sync with `client/src/game/GameScene.ts`. */
export const BUILDING_VISUAL_SCALE = 1.55;
export const BUILDING_VISUAL_WIDTH_FRAC = 1.06;
export const BUILDING_VISUAL_HEIGHT_FRAC = 1.38;
